import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { get, onUnauthorized, post } from './api';
import { demoKind, isDemoKind, setDemoKind } from './demoKind';
import { storageGet, storageSet } from './hooks';
import type { BadgeInfo } from '../../shared/badges';
import { brand } from '../ui/brand';
import { setDisplayTimeZone } from '../../shared/format';

const LAST_EMPLOYEE = 'sos_last_employee';

/** When someone new signs in on a shared tablet, start them on the home screen instead of where the last person left off. */
function homeIfNewPerson(employeeId: string) {
  const prev = storageGet<string | null>(LAST_EMPLOYEE, null);
  if (prev && prev !== employeeId && window.location.hash.replace(/^#\/?/, '') !== '') {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/`);
  }
  storageSet(LAST_EMPLOYEE, employeeId);
}

export interface Me {
  employee: { id: string; name: string; first_name: string; role_id: string; role: string; email: string | null } & BadgeInfo;
  permissions: string[];
  session_kind: 'pin' | 'password';
  register: { id: string; name: string; location_id: string } | null;
  location: { id: string; name: string; tax_rate_ppm: number } | null;
  locations: { id: string; name: string }[];
  settings: Record<string, string>;
  /** Whether this tablet can send card payments to the store's Clover Mini. */
  clover?: { ready: boolean; env: 'sandbox' | 'production'; why: string | null };
}

export interface Status {
  product: string;
  needs_setup: boolean;
  signed_in: boolean;
  device: null | {
    register: { id: string; name: string };
    location: { id: string; name: string };
    employees: ({ id: string; name: string; initials: string; role_id: string; role: string; has_pin: boolean; clocked_in_at: number | null } & BadgeInfo)[];
  };
  business_name: string;
  demo: boolean;
  /** Open stores (only before this device is set up as a store tablet). */
  stores?: { id: string; name: string }[];
  // The public demo (a sample shop; every visitor in their own copy).
  demo_site?: boolean;
  /** The private demo's key hasn't been entered in this browser yet. */
  demo_locked?: boolean;
  demo_shop?: string;
  demo_signup_url?: string;
  demo_copy?: { id: string; started_at: number; expires_at: number; fresh: boolean };
  demo_people?: { name: string; role: string; pin: string; email?: string; password?: string; store?: string }[];
  demo_progress?: { sale: boolean; repair: boolean; buy: boolean; approval?: boolean };
  /** Which sample shop the demo visitor is in: a phone, computer, auto or vacuum repair shop. */
  demo_industry?: 'cellphone' | 'computer' | 'auto' | 'vacuum';
  /** Which release of Store OS the server runs. */
  release?: { commit: string | null; short: string | null; branch: string | null; build: string | null; started_at: number };
  /** Several businesses on this server: which one this is (null: signed out, the Store OS lobby). */
  platform?: {
    lobby: boolean;
    signup: { open: boolean; reason: string | null; test_mode: boolean };
    business: { id: string; name: string; home: boolean } | null;
  };
}

interface SessionValue {
  status: Status | null;
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  lock: () => Promise<void>;
  /** Ends the shift (time clock) and signs out. Returns the hours on the shift. */
  clockOut: () => Promise<number | null>;
  can: (perm: string) => boolean;
}

const Ctx = createContext<SessionValue>(null as unknown as SessionValue);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const st = await get<Status>('/api/status');
      // The demo: the sample shop's brand on the sign-in and home screens. A business that
      // subscribes: its own name (never West Coast Wireless's logo). Signed out: Store OS.
      brand.demo = !!st.demo_site;
      brand.shopName = st.demo_site ? st.demo_shop ?? '' : st.platform?.business?.name ?? '';
      // The demo's computer, auto and vacuum shops wear their own name the way a subscribing business does.
      brand.mode = st.demo_site ? (st.demo_industry && st.demo_industry !== 'cellphone' ? 'business' : 'demo') : !st.platform ? 'home' : st.platform.lobby ? 'lobby' : st.platform.business?.home ? 'home' : 'business';
      document.documentElement.classList.toggle('demo-site', !!st.demo_site);
      // The demo: this tab keeps showing the kind of sample shop it opened, whatever other tabs do.
      if (st.demo_site && !demoKind() && isDemoKind(st.demo_industry)) setDemoKind(st.demo_industry);
      const m = st.signed_in ? await get<Me>('/api/me').catch(() => null) : null;
      if (m) homeIfNewPerson(m.employee.id);
      // Dates and times on every screen in the store's own time zone.
      if (m) setDisplayTimeZone(m.settings?.timezone);
      setStatus(st);
      setMe(m);
    } finally {
      setLoading(false);
    }
  }, []);

  const lock = useCallback(async () => {
    await post('/api/auth/logout').catch(() => undefined);
    setMe(null);
    await refresh();
  }, [refresh]);

  const clockOut = useCallback(async () => {
    const r = await post<{ hours: number | null }>('/api/auth/clock-out').catch(() => null);
    setMe(null);
    await refresh();
    return r?.hours ?? null;
  }, [refresh]);

  useEffect(() => {
    refresh();
    return onUnauthorized(() => {
      setMe(null);
      refresh();
    });
  }, [refresh]);

  const can = useCallback((perm: string) => !!me?.permissions.includes(perm), [me]);

  return <Ctx.Provider value={{ status, me, loading, refresh, lock, clockOut, can }}>{children}</Ctx.Provider>;
}

export function useSession() {
  return useContext(Ctx);
}
